import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import CookieConsent from "react-cookie-consent"
import Buttons from '../Common/Buttons';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { getLocaleOptions, switchLocaleInPathname } from '../../localization/locale';
import { getCurrencyOptions } from '../../localization/currency';


function Footer(){
    const dateString = new Date().getFullYear();
    const history = useHistory();
    const location = useLocation();
    const { locale, setLocalePreference, t, toLocalizedPath } = useLocale();
    const { currency, setCurrencyPreference } = useCurrency();

    function handleLocaleChange(nextLocale) {
        setLocalePreference(nextLocale);
        history.push({
            hash: location.hash,
            pathname: switchLocaleInPathname(location.pathname, nextLocale),
            search: location.search
        });
    }

    return(
        <footer className="spt-90 end-footer">
            <div className="container">
                <div className="footer-columns">
                    <div className="footer-brand-col">
                        <img
                            className="footer-logo"
                            src={`${process.env.PUBLIC_URL}/images/main-logo.svg`}
                            alt="Invoice Point logo"
                        />
                        <p className="footer-tagline">{t('footer.tagline')}</p>
                        <Buttons stacked />
                    </div>

                    <div className="footer-links-col">
                        <h4 className="footer-col-heading">{t('nav.features')}</h4>
                        <ul className="footer-ul">
                            <li className="footer-li">
                                <Link to={toLocalizedPath('aiEstimator')}>{t('nav.aiEstimator')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('invoicing')}>{t('nav.invoicing')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('quotes')}>{t('nav.quotes')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('timeTracking')}>{t('nav.timeTracking.heading')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('payments')}>{t('nav.payments')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('paymentReminders')}>{t('nav.paymentReminders')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('clients')}>{t('nav.clients')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('expenses')}>{t('nav.expenses')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('reports')}>{t('nav.reports')}</Link>
                            </li>
                        </ul>
                    </div>

                    <div className="footer-links-col">
                        <h4 className="footer-col-heading">{t('nav.solutions')}</h4>
                        <ul className="footer-ul">
                            <li className="footer-li">
                                <Link to={toLocalizedPath('freelancers')}>{t('nav.freelancers')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('smallBusiness')}>{t('nav.smallBusiness')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('organization')}>{t('nav.organization')}</Link>
                            </li>
                        </ul>
                    </div>

                    <div className="footer-links-col">
                        <h4 className="footer-col-heading">{t('nav.resources')}</h4>
                        <ul className="footer-ul">
                            <li className="footer-li">
                                <Link to={toLocalizedPath('howItWorks')}>{t('nav.howItWorks')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('helpCenter')}>{t('nav.helpCenter')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('templates')}>{t('nav.templates')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('guides')}>{t('nav.guides')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('blog')}>{t('nav.blog')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('contact')}>{t('nav.contact')}</Link>
                            </li>
                        </ul>
                    </div>

                    <div className="footer-links-col">
                        <h4 className="footer-col-heading">{t('footer.companyHeading')}</h4>
                        <ul className="footer-ul">
                            <li className="footer-li">
                                <Link to={toLocalizedPath('home')}>{t('nav.home')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('pricing')}>{t('nav.pricing')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('faqs')}>{t('nav.faqs')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('privacyPolicy')}>{t('nav.privacy')}</Link>
                            </li>
                            <li className="footer-li">
                                <Link to={toLocalizedPath('termsOfService')}>{t('nav.terms')}</Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="footer-divider"></div>
                <div className="footer-bottom">
                    <p className="footer-copyright">
                        {t('footer.copyright', { year: dateString })}
                    </p>
                    <p className="footer-copyright footer-copyright-muted">
                        {t('footer.allRightsReserved')}
                    </p>
                    <div className="footer-language-block">
                        <div className="language-switcher" aria-label={t('footer.currencyLabel')}>
                            <label className="language-switcher-label" htmlFor="footer-currency-select">
                                {t('footer.currencyLabel')}
                            </label>
                            <select
                                className="language-switcher-select"
                                id="footer-currency-select"
                                onChange={(event) => setCurrencyPreference(event.target.value)}
                                value={currency}
                            >
                                {getCurrencyOptions().map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="language-switcher" aria-label={t('footer.languageLabel')}>
                            <label className="language-switcher-label" htmlFor="footer-language-select">
                                {t('footer.languageLabel')}
                            </label>
                            <select
                                className="language-switcher-select"
                                id="footer-language-select"
                                onChange={(event) => handleLocaleChange(event.target.value)}
                                value={locale}
                            >
                                {getLocaleOptions().map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <CookieConsent
                buttonText={t('cookie.button')}
                style={{ background: "rgb(25, 29, 40, 0.9)" }}
                buttonStyle={{ background: "#FFF", color: "#000", fontSize: "16px" }}>
                {t('cookie.messageBeforeLink')}<Link to={toLocalizedPath('cookiePolicy')}>{t('cookie.linkLabel')}</Link>{t('cookie.messageAfterLink')}
            </CookieConsent>
        </footer>
    )
}
export default Footer;